import { useEffect, useMemo, useState } from "react";
import {
  computeRecurrence,
  costRollup,
  localCalendarDate,
  rollupTotal,
  type ActivityField,
  type ActivityLog,
  type ActivityType,
  type Membership,
} from "@lifelog/shared";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { formatCurrencyDollars, formatFieldValue } from "../lib/export.js";
import { mediaUrl } from "../lib/photo.js";
import { ListSkeleton } from "./Skeleton.js";
import { ModalShell } from "./ModalShell.js";
import { CountCalendar } from "./report/CountCalendar.js";
import { CountChart, TrendChart } from "./report/CountChart.js";
import { ReportRangeSelector } from "./report/ReportRangeSelector.js";
import {
  datesInclusive,
  prevWindowDates,
  weekStartSunday,
  windowDates,
  type ReportRange,
} from "./report/windows.js";

type Tab = "calendar" | "chart" | "stats";
type Granularity = "day" | "week" | "month";
type ChartMode = "count" | "trend";

const LOOKBACK_MS = 3 * 365 * 24 * 60 * 60 * 1000;

function numericFields(type: ActivityType): ActivityField[] {
  return [...type.fields]
    .filter((f) => f.kind === "number" || f.kind === "duration" || f.kind === "currency")
    .sort((a, b) => a.order - b.order);
}

function defaultGranularity(range: ReportRange): Granularity {
  if (range === 365) return "week";
  if (range === "all") return "month";
  return "day";
}

function inWindow(iso: string, tz: string, start: string, end: string): boolean {
  const d = localCalendarDate(iso, tz);
  return d >= start && d <= end;
}

function bucketLabel(date: string, gran: Granularity): string {
  if (gran === "month") {
    return new Date(date + "-01T00:00:00Z").toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  const dt = new Date(date + "T00:00:00Z");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function streaks(days: string[], counts: Map<string, number>): { longest: number; current: number } {
  let longest = 0;
  let run = 0;
  for (const d of days) {
    if ((counts.get(d) ?? 0) > 0) {
      run += 1;
      longest = Math.max(longest, run);
    } else {
      run = 0;
    }
  }
  let current = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    if ((counts.get(days[i]!) ?? 0) > 0) current += 1;
    else break;
  }
  return { longest, current };
}

export function ItemReportModal({
  type,
  timezone,
  members,
  onClose,
}: {
  type: ActivityType;
  timezone: string;
  members: Membership[];
  onClose: () => void;
}) {
  const [rawLogs, setRawLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("calendar");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [range, setRange] = useState<ReportRange>(90);
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [chartMode, setChartMode] = useState<ChartMode>("count");
  const numerics = useMemo(() => numericFields(type), [type]);
  const [trendFieldId, setTrendFieldId] = useState<string | null>(numerics[0]?.fieldId ?? null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const from = new Date(Date.now() - LOOKBACK_MS).toISOString();
    const to = new Date().toISOString();
    void (async () => {
      try {
        const res = await api<{ logs: ActivityLog[] }>(
          `/logs?typeId=${encodeURIComponent(type.activityTypeId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=500`,
        );
        if (!cancelled) setRawLogs(res.logs);
      } catch (err) {
        if (cancelled) return;
        if (isOfflineError(err)) setError("You're offline. Try again when you're back online.");
        else setError(err instanceof ApiError ? err.message : "Couldn't load this activity.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type.activityTypeId]);

  useEffect(() => {
    setGranularity(defaultGranularity(range));
  }, [range]);

  useEffect(() => {
    if (!numerics.some((f) => f.fieldId === trendFieldId)) {
      setTrendFieldId(numerics[0]?.fieldId ?? null);
    }
    if (numerics.length === 0 && chartMode === "trend") setChartMode("count");
  }, [numerics, trendFieldId, chartMode]);

  const tz = timezone;
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const mem of members) m.set(mem.userId, mem.displayName);
    return m;
  }, [members]);
  const nameOf = (id: string | undefined) => (id ? (nameById.get(id) ?? id) : "");

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const log of rawLogs) {
      const d = localCalendarDate(log.occurredAt, tz);
      m.set(d, (m.get(d) ?? 0) + 1);
    }
    return m;
  }, [rawLogs, tz]);

  const today = localCalendarDate(new Date().toISOString(), tz);
  const firstDate = useMemo(() => {
    let min: string | null = null;
    for (const d of counts.keys()) if (!min || d < min) min = d;
    return min;
  }, [counts]);

  const rangeDays = range === "all" ? null : range;
  const rangeDates = useMemo(() => {
    if (rangeDays) return windowDates(rangeDays, tz);
    if (!firstDate) return [today];
    return datesInclusive(firstDate, today);
  }, [rangeDays, tz, firstDate, today]);

  const prevWindow = useMemo(() => {
    if (rangeDays) return prevWindowDates(rangeDays, tz);
    return [];
  }, [rangeDays, tz]);

  const windowStart = rangeDates[0] ?? today;
  const windowEnd = rangeDates[rangeDates.length - 1] ?? today;

  const windowLogs = useMemo(
    () => rawLogs.filter((l) => inWindow(l.occurredAt, tz, windowStart, windowEnd)),
    [rawLogs, tz, windowStart, windowEnd],
  );

  const windowCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const log of windowLogs) {
      const d = localCalendarDate(log.occurredAt, tz);
      m.set(d, (m.get(d) ?? 0) + 1);
    }
    return m;
  }, [windowLogs, tz]);

  const chartDays = useMemo(() => {
    if (granularity === "day") {
      return rangeDates.map((date) => ({ date, count: windowCounts.get(date) ?? 0 }));
    }
    const buckets = new Map<string, number>();
    const order: string[] = [];
    for (const date of rangeDates) {
      const key = granularity === "week" ? weekStartSunday(date) : date.slice(0, 7);
      if (!buckets.has(key)) {
        buckets.set(key, 0);
        order.push(key);
      }
      buckets.set(key, (buckets.get(key) ?? 0) + (windowCounts.get(date) ?? 0));
    }
    return order.map((date) => ({
      date,
      count: buckets.get(date) ?? 0,
      label: bucketLabel(date, granularity),
    }));
  }, [rangeDates, windowCounts, granularity]);

  const trendField = numerics.find((f) => f.fieldId === trendFieldId) ?? numerics[0];
  const trendPoints = useMemo(() => {
    if (!trendField) return [];
    const points: { date: string; value: number }[] = [];
    const sorted = [...windowLogs].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    for (const log of sorted) {
      const raw = log.values[trendField.fieldId];
      if (typeof raw !== "number") continue;
      const value = trendField.kind === "currency" ? raw / 100 : raw;
      points.push({ date: localCalendarDate(log.occurredAt, tz), value });
    }
    return points;
  }, [windowLogs, trendField, tz]);

  const recurrence = useMemo(
    () => computeRecurrence(windowLogs.map((l) => l.occurredAt), tz),
    [windowLogs, tz],
  );

  const spendGroups = useMemo(() => costRollup(windowLogs, [type], tz), [windowLogs, type, tz]);

  const allRecurrence = useMemo(
    () => computeRecurrence(rawLogs.map((l) => l.occurredAt), tz),
    [rawLogs, tz],
  );

  const streak = useMemo(() => streaks(rangeDates, windowCounts), [rangeDates, windowCounts]);

  const prevTotal = useMemo(() => {
    if (prevWindow.length === 0) return null;
    const start = prevWindow[0]!;
    const end = prevWindow[prevWindow.length - 1]!;
    return rawLogs.filter((l) => inWindow(l.occurredAt, tz, start, end)).length;
  }, [prevWindow, rawLogs, tz]);

  const memberRows = useMemo(() => {
    const used = windowLogs.some((l) => l.performedByUserId);
    if (!used) return [];
    const map = new Map<string, number>();
    for (const log of windowLogs) {
      const id = log.performedByUserId ?? log.loggedByUserId;
      map.set(id, (map.get(id) ?? 0) + 1);
    }
    return [...map.entries()]
      .map(([id, n]) => ({ id, name: nameById.get(id) ?? id, n }))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [windowLogs, nameById]);

  const dayLogs = useMemo(() => {
    if (!selectedDay) return [];
    return rawLogs
      .filter((l) => localCalendarDate(l.occurredAt, tz) === selectedDay)
      .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  }, [rawLogs, selectedDay, tz]);

  const currentTotal = windowLogs.length;
  const trendDelta =
    prevTotal === null ? null : currentTotal === prevTotal ? 0 : currentTotal > prevTotal ? 1 : -1;

  const subheader = (
    <div className="flex gap-2">
      {(["calendar", "chart", "stats"] as Tab[]).map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setTab(t)}
          className={[
            "min-h-[44px] rounded-full px-3 text-sm font-medium capitalize transition-colors sm:min-h-0 sm:py-1",
            tab === t
              ? "bg-(--accent) text-(--accent-contrast)"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700",
          ].join(" ")}
        >
          {t === "calendar" ? "Calendar" : t === "chart" ? "Chart" : "Stats"}
        </button>
      ))}
    </div>
  );

  return (
    <ModalShell title={type.name} subtitle={type.shortName} size="2xl" subheader={subheader} onClose={onClose}>
      {loading && <ListSkeleton rows={4} lines={1} />}
      {!loading && error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && tab === "calendar" && (
        <>
          {rawLogs.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">
              No entries recorded for {type.name}.
            </p>
          ) : (
            <>
              <CountCalendar
                counts={counts}
                timezone={tz}
                selectedDay={selectedDay}
                onDayClick={(date) => setSelectedDay((prev) => (prev === date ? null : date))}
              />
              {selectedDay && (
                <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-800">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {new Date(selectedDay + "T12:00:00Z").toLocaleDateString(undefined, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      timeZone: "UTC",
                    })}
                  </h3>
                  {dayLogs.length === 0 ? (
                    <p className="text-sm text-gray-400">No entries this day.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {dayLogs.map((log) => (
                        <li
                          key={log.logId}
                          className="flex items-start gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm dark:border-gray-800"
                        >
                          <span className="w-14 shrink-0 text-xs text-gray-400 dark:text-gray-500">
                            {new Date(log.occurredAt).toLocaleTimeString(undefined, {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                          <LogValues type={type} log={log} nameOf={nameOf} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {!loading && !error && tab === "chart" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setChartMode("count")}
                className={[
                  "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                  chartMode === "count"
                    ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                ].join(" ")}
              >
                Count
              </button>
              {numerics.length > 0 && (
                <button
                  type="button"
                  onClick={() => setChartMode("trend")}
                  className={[
                    "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                    chartMode === "trend"
                      ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                  ].join(" ")}
                >
                  Trend
                </button>
              )}
            </div>
            <ReportRangeSelector value={range} onChange={setRange} />
          </div>

          {chartMode === "count" && (
            <>
              <div className="flex rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
                {(["day", "week", "month"] as Granularity[]).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGranularity(g)}
                    className={[
                      "min-h-[44px] rounded-full px-3 capitalize transition-all sm:min-h-0 sm:py-1",
                      granularity === g
                        ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                    ].join(" ")}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <CountChart days={chartDays} />
            </>
          )}

          {chartMode === "trend" && trendField && (
            <>
              {numerics.length > 1 && (
                <select
                  value={trendField.fieldId}
                  onChange={(e) => setTrendFieldId(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  {numerics.map((f) => (
                    <option key={f.fieldId} value={f.fieldId}>
                      {f.label}
                    </option>
                  ))}
                </select>
              )}
              <TrendChart
                points={trendPoints}
                unit={trendField.kind === "currency" ? trendField.unit ?? "" : trendField.unit}
              />
            </>
          )}
        </div>
      )}

      {!loading && !error && tab === "stats" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <ReportRangeSelector value={range} onChange={setRange} />
            {trendDelta !== null && (
              <span
                className={
                  "text-sm font-medium " +
                  (trendDelta > 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : trendDelta < 0
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-gray-500")
                }
                title={
                  prevTotal === null
                    ? undefined
                    : `Previous window: ${prevTotal} vs this window: ${currentTotal}`
                }
              >
                {trendDelta > 0 ? "↑" : trendDelta < 0 ? "↓" : "→"}{" "}
                {trendDelta > 0 ? "busier" : trendDelta < 0 ? "quieter" : "same"} vs prev
              </span>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <Stat label="Total" value={String(recurrence.occurrenceCount)} />
            <Stat
              label="First"
              value={recurrence.firstAt ? localCalendarDate(recurrence.firstAt, tz) : "—"}
            />
            <Stat
              label="Last"
              value={recurrence.lastAt ? localCalendarDate(recurrence.lastAt, tz) : "—"}
            />
            <Stat
              label="Median interval"
              value={
                recurrence.medianIntervalDays !== undefined
                  ? `${round1(recurrence.medianIntervalDays)} d`
                  : "—"
              }
            />
            <Stat
              label="Mean ± stdev"
              value={
                recurrence.meanIntervalDays !== undefined
                  ? `${round1(recurrence.meanIntervalDays)} ± ${round1(recurrence.intervalStdDevDays ?? 0)} d`
                  : "—"
              }
            />
            <Stat
              label="Current gap"
              value={
                allRecurrence.daysSinceLast !== undefined ? `${allRecurrence.daysSinceLast} d` : "—"
              }
            />
            <Stat label="Due" value={dueLabel(allRecurrence.dueStatus, allRecurrence.overdueByDays)} />
            <Stat label="Longest streak" value={`${streak.longest} d`} />
            <Stat label="Current streak" value={`${streak.current} d`} />
            {spendGroups.map((g) => (
              <Stat
                key={g.currency}
                label={`Spend (${g.currency})`}
                value={`${formatCurrencyDollars(rollupTotal(g))} ${g.currency}`}
              />
            ))}
          </dl>

          {recurrence.regularity !== undefined && (
            <div>
              <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Regularity</span>
                <span>{Math.round(recurrence.regularity * 100)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full bg-(--accent)"
                  style={{ width: `${Math.round(recurrence.regularity * 100)}%` }}
                />
              </div>
            </div>
          )}

          {memberRows.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Performed by
              </h3>
              <ul className="space-y-1 text-sm">
                {memberRows.map((row) => (
                  <li key={row.id} className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span>{row.name}</span>
                    <span className="text-gray-500">{row.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </ModalShell>
  );
}

function round1(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

function dueLabel(status: string, overdueByDays?: number): string {
  if (status === "overdue") return overdueByDays !== undefined ? `overdue ${overdueByDays}d` : "overdue";
  if (status === "due_soon") return "due soon";
  if (status === "ok") return "ok";
  return "unknown";
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 px-3 py-2 dark:border-gray-800">
      <dt className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function LogValues({
  type,
  log,
  nameOf,
}: {
  type: ActivityType;
  log: ActivityLog;
  nameOf: (id: string | undefined) => string;
}) {
  const fields = [...type.fields].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const field of fields) {
    const value = log.values[field.fieldId];
    if (value === undefined) continue;
    parts.push(`${field.label}: ${formatFieldValue(field, value)}`);
  }
  const who = nameOf(log.performedByUserId);
  return (
    <div className="min-w-0 flex-1">
      {parts.length > 0 && <p className="text-gray-800 dark:text-gray-200">{parts.join(" · ")}</p>}
      {log.note && <p className="text-gray-600 dark:text-gray-400">{log.note}</p>}
      {who && <p className="text-xs text-gray-400">by {who}</p>}
      {log.photoKey && (
        <a href={mediaUrl(log.photoKey)} target="_blank" rel="noreferrer" className="mt-2 block">
          <img
            src={mediaUrl(log.photoKey)}
            alt=""
            className="max-h-64 w-full rounded-lg object-contain"
          />
        </a>
      )}
      {parts.length === 0 && !log.note && !log.photoKey && (
        <p className="text-gray-500 dark:text-gray-400">{type.shortName}</p>
      )}
    </div>
  );
}
